import classNames from 'classnames'
import styles from './ProducerMarker.module.scss'
import { ProducerMapMarkerProps } from './types'
import { Icon } from '@boltenergy-be/design-system'
import { getEnergyTypeIcon } from 'utils/producers'
import { AdvancedMarker, AdvancedMarkerAnchorPoint, InfoWindow, useAdvancedMarkerRef } from '@vis.gl/react-google-maps'
import { useCallback, useMemo, useState } from 'react'
import ProducerCard from 'components/producer-card/ProducerCard'
import { useGetLanguage } from 'hooks/useGetLanguage'

const ProducerMarker = ({ producer, onClick, onMouseEnter, onMouseLeave, active, index }: ProducerMapMarkerProps) => {
  // i18n
  const language = useGetLanguage()

  // Marker ref
  const [markerRef, marker] = useAdvancedMarkerRef()

  // Show info window
  const [infoWindowShown, setInfoWindowShown] = useState(false)

  // Marker is selected
  const [activeMarker, setActiveMarker] = useState(false)

  // A new object identity makes @vis.gl re-assign AdvancedMarkerElement.position on every render, which throws
  // inside the Maps SDK when the element was detached from the map in that same commit
  const position = useMemo(
    () => ({ lat: Number(producer.coordinates.lat), lng: Number(producer.coordinates.long) }),
    [producer.coordinates.lat, producer.coordinates.long]
  )

  // Handles the marker click to open the info window
  const handleMarkerClick = useCallback(() => setInfoWindowShown((isShown) => !isShown), [])

  // Handles the info window close
  const handleClose = useCallback(() => setInfoWindowShown(false), [])

  return (
    <>
      <AdvancedMarker
        key={index}
        position={position}
        anchorPoint={AdvancedMarkerAnchorPoint['BOTTOM']}
        zIndex={activeMarker ? 20 : 10}
        onClick={() => {
          handleMarkerClick()
          if (onClick) {
            onClick(producer, index)
          }
        }}
        ref={markerRef}
        onMouseEnter={() => {
          !activeMarker && !infoWindowShown && setActiveMarker(true)
          !infoWindowShown && onMouseEnter && onMouseEnter(producer, index)
        }}
        onMouseLeave={() => {
          activeMarker && !infoWindowShown && setActiveMarker(false)
          !infoWindowShown && onMouseLeave && onMouseLeave()
        }}
      >
        <div className={classNames(styles['producer-marker'], { [styles['is-active']]: active, [styles.hidden]: infoWindowShown })}>
          <Icon name={getEnergyTypeIcon(producer.energyType)} />
          {active && (
            <>
              &nbsp;
              <span className={styles.name}>{producer.name[language]}</span>
            </>
          )}
        </div>
      </AdvancedMarker>

      {infoWindowShown && (
        <InfoWindow anchor={marker} onClose={handleClose}>
          <ProducerCard className={styles['producer-marker-card']} hideButton hideDescription producer={producer} />
        </InfoWindow>
      )}
    </>
  )
}

export default ProducerMarker
